/**
 * COMPTABILITÉ — CE QUE LE COCKPIT, LA LISTE ET L'ASSISTANT PARTAGENT.
 *
 * Trois écrans lisent les mêmes objets (une nuit, une clôture, un point de
 * vente, un document source) et posent les mêmes questions honnêtes : « cette
 * donnée est-elle arrivée ? », « ce chiffre est-il définitif ? », « ce motif
 * fait-il douze caractères ? ». Écrire trois fois ces réponses aurait produit
 * trois formulations différentes du même fait — exactement ce que le domaine
 * reproche aux classeurs qu'il remplace.
 *
 * ═══ POURQUOI DES TYPES RECOPIÉS ═══
 * `apps/web` n'a AUCUNE dépendance interne : le navigateur ne peut pas importer
 * `packages/core`. Les formes ci-dessous sont donc le miroir assumé de
 * `packages/core/src/finance/*` — comme `money.ts` est le miroir de
 * `core/src/finance/money.ts`. Elles ne servent qu'à LIRE une réponse HTTP :
 * aucune règle métier ne vit ici.
 *
 * ═══ POURQUOI UNE LECTURE TOLÉRANTE ═══
 * Les routes `/api/v1/finance/*` sont écrites en parallèle. Un écran qui
 * planterait sur une route encore absente serait pire qu'inutile : il ferait
 * croire à une panne. `lireJson` distingue donc trois états — en cours,
 * disponible, absente avec sa raison — et AUCUN d'eux ne vaut zéro.
 */

import type { AssociationsVue } from './BilletterieAssociations.js'
import { useEffect, useState, type ReactNode } from 'react'
import { apiGet } from '../admin/primitives.js'
import { formatEcart, formatMoney, parseMoney, type Cents, tonSeuil } from './money.js'
import type { NightPresalesRow, OffNightSaleRow } from './nights-common.js'

/* ═══════════════════════ le vocabulaire (miroir du cœur) ═══════════════════ */

export type ClosingStatus = 'DRAFT' | 'READY' | 'CLOSED' | 'SUPERSEDED'

export const CLOSING_STATUS_LABELS: Record<ClosingStatus, string> = {
  DRAFT: 'Brouillon',
  READY: 'Prête à finaliser',
  CLOSED: 'Finalisée',
  SUPERSEDED: 'Remplacée par une révision',
}

/** La pastille du design system qui correspond à chaque état. */
export const CLOSING_STATUS_PILL: Record<ClosingStatus, string> = {
  DRAFT: 'lc-pill lc-pill--warning',
  READY: 'lc-pill',
  CLOSED: 'lc-pill lc-pill--ok',
  SUPERSEDED: 'lc-pill lc-pill--idle',
}

export type SourceProvider = 'ZAP_SIS' | 'SHOTGUN' | 'MANUEL' | 'AUTRE'

export const SOURCE_PROVIDER_LABELS: Record<SourceProvider, string> = {
  ZAP_SIS: 'ZAP',
  SHOTGUN: 'Shotgun',
  MANUEL: 'Dépôt manuel',
  AUTRE: 'Autre',
}

export type SourceParseStatus =
  | 'RECEIVED' | 'PARSING' | 'PARSED' | 'NEEDS_REVIEW' | 'FAILED' | 'DUPLICATE'

export const SOURCE_PARSE_LABELS: Record<SourceParseStatus, string> = {
  RECEIVED: 'Reçu',
  PARSING: 'Lecture en cours',
  PARSED: 'Lu',
  NEEDS_REVIEW: 'À vérifier',
  FAILED: 'Lecture impossible',
  DUPLICATE: 'Doublon',
}

export const SOURCE_PARSE_PILL: Record<SourceParseStatus, string> = {
  RECEIVED: 'lc-pill lc-pill--idle',
  PARSING: 'lc-pill lc-pill--idle',
  PARSED: 'lc-pill lc-pill--ok',
  NEEDS_REVIEW: 'lc-pill lc-pill--warning',
  FAILED: 'lc-pill lc-pill--error',
  DUPLICATE: 'lc-pill lc-pill--idle',
}

export type DiscrepancyStatus = 'OUVERT' | 'EXPLIQUE' | 'ACCEPTE' | 'RESOLU'

export const DISCREPANCY_STATUS_LABELS: Record<DiscrepancyStatus, string> = {
  OUVERT: 'Ouvert',
  EXPLIQUE: 'Expliqué',
  ACCEPTE: 'Accepté',
  RESOLU: 'Résolu',
}

/**
 * La pastille de chaque état d'écart.
 *
 * Seul « ouvert » alerte. Un écart expliqué, accepté ou résolu est un fait
 * comptable TRAITÉ : le peindre en rouge trois mois de suite pousserait à le
 * faire disparaître plutôt qu'à l'expliquer.
 */
export const DISCREPANCY_STATUS_PILL: Record<DiscrepancyStatus, string> = {
  OUVERT: 'lc-pill lc-pill--warning',
  EXPLIQUE: 'lc-pill',
  ACCEPTE: 'lc-pill lc-pill--ok',
  RESOLU: 'lc-pill lc-pill--idle',
}

/* ═══════════════════════ les formes lues sur le réseau ═══════════════════ */

export interface Soiree { id: string; name: string; starts_at: string; status: 'SCHEDULED' | 'CANCELLED' }

/**
 * LA BILLETTERIE EST UNE VALEUR FACIALE, JAMAIS UN CHIFFRE D'AFFAIRES.
 *
 * La source ne donne ni le montant encaissé, ni les frais du fournisseur, ni
 * le net reversé, ni le montant d'un remboursement. Elle donne le prix affiché
 * sur les billets, et rien d'autre. Le serveur rend `null` quand la session n'a
 * pas le droit de lire ces montants — une absence de droit qui se déguiserait
 * en zéro serait un chiffre faux.
 */
export interface Billetterie {
  available: boolean
  online_cents: Cents
  onsite_cents: Cents
  tickets: number
  free_tickets: number
  refunded_tickets: number
  /** Les défauts connus de cette nuit, en français, à afficher AVEC le chiffre. */
  caveats: string[]
}

export interface CurrentNight {
  business_date: string
  soirees: Soiree[]
  sources: Array<{
    id: string; provider: SourceProvider; filename: string
    parse_status: SourceParseStatus; received_at: string
  }>
  closing: {
    id: string; status: ClosingStatus; revision: number; version: number
    opened_at: string; opened_by_name: string | null
  } | null
  /** `null` sans le droit de lire les montants en direct. Jamais zéro. */
  ticketing?: Billetterie | null
}

/**
 * UNE NUIT PASSÉE QUI ATTEND ENCORE QUELQUE CHOSE.
 *
 * Rien ne répondait à « quelles nuits n'ont pas été clôturées ? » : la nuit du
 * moment a son écran, la liste des clôtures ne montre que les nuits DÉJÀ
 * ouvertes. Une nuit oubliée ne manquait donc à personne jusqu'au
 * rapprochement bancaire, trois mois plus tard.
 */
export interface NuitOuvrable {
  business_date: string
  soirees: Soiree[]
  source_count: number
  source_parsed_count: number
  /** La clôture qui fait foi, `null` si personne ne l'a encore ouverte. */
  closing: { id: string; status: ClosingStatus; revision: number; version: number } | null
}

export interface ClosingRow {
  id: string
  business_date: string
  revision: number
  status: ClosingStatus
  version: number
  opened_at: string
  closed_at: string | null
  closed_by_name: string | null
  retained_cents: Cents
  open_discrepancies: number
}

export interface ClosingLineView {
  id: string
  closing_pos_id: string | null
  register_id: string | null
  register_name: string | null
  terminal_id: string | null
  terminal_name: string | null
  payment_method: string
  payment_method_label: string
  source_cents: Cents
  source_reading_id: string | null
  observed_cents: Cents
  retained_cents: Cents
  /**
   * Les ventes du guichet d'une autre nuit, décidées, sur cette ligne (Accueil
   * seulement) : positif = rattachées ici. Absent d'un serveur d'avant le
   * 08/10/2026 : lu comme 0.
   */
  transfer_cents?: Cents
  /** L'ATTENDU : la source corrigée de ces ventes. Absent : la source. */
  expected_cents?: Cents
  effective_cents: Cents
  effective_origin: string
  override_reason: string | null
  override_by_name: string | null
  override_at: string | null
}

export interface TpeTicketView {
  id: string
  terminal_id: string | null
  terminal_name: string | null
  amount_cents: number
  operator_person_id: string | null
  operator_name: string | null
  operator_label: string | null
}

export interface ClosingPosView {
  /** L'identifiant de la LIGNE DE PÉRIMÈTRE, pas celui du point de vente. */
  id: string
  point_of_sale_id: string
  point_of_sale_name: string
  point_of_sale_kind: string
  location_name: string
  /** `null` = la question n'a pas encore été posée. Ce n'est PAS « non ». */
  was_open: boolean | null
  /** Ce que Gestion propose. Une suggestion, jamais une réponse. */
  presumed_open: boolean | null
  step_done: boolean
  cash_counts: Array<{ denomination_cents: number; quantity: number }>
  cash_counted_cents: Cents
  cash_total_override_cents: Cents
  cash_total_override_reason: string | null
  cash_effective_cents: Cents
  note: string | null
  lines: ClosingLineView[]
  totals: { source_cents: Cents; observed_cents: Cents; retained_cents: Cents }
  /** Les tickets TPE ; leur somme est le constaté « Carte ». */
  tpe_tickets: TpeTicketView[]
}

export interface DiscrepancyView {
  id: string
  closing_pos_id: string | null
  point_of_sale_name: string | null
  amount_cents: Cents
  reason_code: string | null
  reason_label: string | null
  status: DiscrepancyStatus
  note: string | null
  resolved_by_name: string | null
  resolved_at: string | null
}

export interface ClosingDetail {
  id: string
  business_date: string
  revision: number
  supersedes_id: string | null
  status: ClosingStatus
  version: number
  opened_at: string
  opened_by_name: string | null
  closed_at: string | null
  closed_by_name: string | null
  revision_reason: string | null
  /** Qui a écrit en dernier. Informatif : la lecture n'est jamais bloquée. */
  locked_by_name: string | null
  locked_at: string | null
  /** Calculé par le serveur : comparer un NOM côté navigateur serait fragile. */
  locked_is_me: boolean
  soirees: Soiree[]
  pos: ClosingPosView[]
  discrepancies: DiscrepancyView[]
  components: Array<{ component_code: string; label: string; point_of_sale_id: string | null; amount_cents: Cents; origin: string }>
  totals: {
    source_cents: Cents
    observed_cents: Cents
    retained_cents: Cents
    cash_cents: Cents
    by_payment_method: Array<{ payment_method: string; label: string; retained_cents: Cents }>
    /**
     * Le sur-place arrêté (ou que la finalisation arrêtera), hors offerts et
     * hors préventes (11/10/2026). Absent d'un serveur d'avant : « non servi ».
     */
    onsite_cents?: Cents
    /** Les offerts, rangés à part. */
    offers_cents?: Cents
    /** Sur place + préventes reversées ; `null` = reversé inconnu ou masqué. */
    official_cents?: Cents
  }
  /** Ce qui empêche encore de finaliser. Vide = prête. */
  blockers: string[]
  /**
   * Les préventes en ligne de la nuit (11/10/2026) : lues en direct jusqu'à
   * la finalisation, figées par elle (composante « Billetterie en ligne »).
   * Absent d'un serveur d'avant : « non servi ».
   */
  presales?: ClosingPresalesRow | null
  /** Valeur faciale de la billetterie de la nuit. `null` sans le droit. */
  ticketing?: Billetterie | null
  /**
   * LES ENTRÉES DE LA NUIT — billets Shotgun relevés automatiquement, et la
   * guestlist saisie à la main (l'API Shotgun ne la donne pas).
   * (Contrat posé le 07/10/2026 ; rempli par le chantier « entrées ».)
   */
  entries?: {
    shotgun_valid: number; shotgun_scanned: number
    guestlist_count: number | null; guestlist_note: string | null
    total: number
    /** Les billets des associations (Pretix) vendus, 16/10/2026 ; absents d'un serveur d'avant. */
    associations_sold?: number; associations_scanned?: number; associations_counted?: boolean
    associations_events?: Array<{ event_id: string; name: string; billetterie: string; sold: number; scanned: number }>
  } | null
  /** La billetterie des associations (Pretix), 16/10/2026 : en direct, puis figée par la finalisation. */
  associations?: AssociationsVue | null
  /** Les ventes du guichet d'une autre nuit qui touchent celle-ci (08/10/2026). */
  off_night?: ClosingOffNight | null
  /**
   * La validation en bloc que la finalisation remplacerait (16/10/2026).
   * Absent d'un serveur d'avant, `null` sans validation en vigueur.
   */
  validation_replacement?: ValidationRemplaceeVue | null
  /**
   * Ce que la finalisation a fait de la validation (16/10/2026) : la trace de
   * la première finalisation de la nuit qui l'a remplacée, redite sur la
   * clôture finalisée, ses révisions et la fiche de nuit.
   */
  replaced_validation?: TraceRemplacementVue | null
}

/** La trace du remplacement, telle que le serveur la sert (montants `null` sans le droit). */
export interface TraceRemplacementVue {
  sentence: string
  reason: string | null
  confirmation_required: boolean
  validated_cents: number | null
  ticketing_dropped_cents: number | null
  closing_cents: number | null
  drop_cents: number | null
  history_label: string | null
  validated_by_name: string | null
  validated_at: string
  closing_id: string
  revision: number
  replaced_at: string | null
  replaced_by_name: string | null
}

/**
 * Ce que le serveur dit de la validation que la finalisation remplacerait.
 * Les montants sont `null` sans le droit de les lire ; la phrase, elle, est
 * toujours servie (sans chiffre dans ce cas).
 */
export interface ValidationRemplaceeVue {
  /** Vrai = la finalisation exige un motif (la baisse dépasse le seuil des Règles). */
  requires_confirmation: boolean
  /**
   * Vrai = le chiffre que la clôture arrêterait est connu. Faux = préventes
   * pas encore connues : l'écran n'annonce alors ni baisse ni billetterie
   * perdue, qui ne sont pas établies.
   */
  closing_known: boolean
  sentence: string
  validated_cents: number | null
  validated_ticketing_cents: number | null
  ticketing_dropped_cents: number | null
  closing_cents: number | null
  drop_cents: number | null
  threshold_cents: number
  history_label: string | null
  validated_by_name: string | null
  validated_at: string
}

/** Les préventes vues de la clôture : la forme de la fiche de nuit, plus l'état du gel. */
export interface ClosingPresalesRow extends NightPresalesRow {
  /** Figées par la finalisation : le montant est celui de la composante, plus celui de la billetterie. */
  frozen: boolean
  frozen_at: string | null
  /** Connu du serveur, caché par les droits : « montant réservé », jamais 0. */
  masked?: boolean
}

/**
 * Les ventes du guichet d'une autre nuit, vues de la clôture : les cas (avec
 * leur décision) et ce que les décisions font à l'attendu de l'Accueil — déjà
 * porté par ses lignes (`transfer_cents`, `expected_cents`).
 */
export interface ClosingOffNight {
  accueil_pos_id: string | null
  cases: OffNightSaleRow[]
  transfer_cb_cents: number
  transfer_cash_cents: number
  unplaced_cents: number
  /** Une clôture arrêtée applique les décisions de son instant ; `null` = maintenant. */
  as_of: string | null
}

/**
 * CE QUE L'ASSISTANT PRÊTE À CHACUNE DE SES ÉTAPES.
 *
 * Une étape ne connaît ni le routeur, ni la concurrence optimiste, ni le
 * step-up : elle décrit un geste, la coquille se charge de l'écrire. C'est ce
 * qui permet d'ajouter une étape sans redécouvrir comment on enregistre.
 */
export interface ClosingApi {
  closing: ClosingDetail
  settings: FinanceSettingsView
  /** Vrai si la session détient cette permission. Pilote la VISIBILITÉ, jamais le droit. */
  peut: (cle: string) => boolean
  /**
   * Écrit, gère le conflit de version et rafraîchit. Rend vrai si c'est passé.
   * `surRefus` reçoit le code d'erreur du serveur AVANT le traitement par
   * défaut ; vrai = l'étape s'en charge (ni toast, ni bandeau de conflit).
   */
  ecrire: (
    method: string, chemin: string, corps: Record<string, unknown>,
    surRefus?: (erreur: string | undefined) => boolean,
  ) => Promise<boolean>
  recharger: () => void
}

export interface SourceDocumentRow {
  id: string
  provider: SourceProvider
  report_type: string
  business_date: string | null
  received_at: string
  filename: string
  parse_status: SourceParseStatus
  parse_note: string | null
  uploaded_by_name: string | null
  /** Décidé par le SERVEUR : à reprendre, hors annexes ZAP et hors rapports écartés. */
  needs_attention?: boolean
  /** Écarté par un humain (entretien, test) : ne compte plus nulle part. */
  ignored?: boolean
  ignored_reason?: string | null
  /** Le courriel d'origine, quand le document est arrivé par la relève. */
  email_sender?: string | null
  email_subject?: string | null
  email_received_at?: string | null
  /** Synthèse ZAP « [mode offline] » : un complément de la nuit. */
  zap_offline?: boolean
}

/** « À reprendre » : la décision du serveur quand il la donne, sinon le statut de lecture. */
export function aReprendre(d: SourceDocumentRow): boolean {
  if (typeof d.needs_attention === 'boolean') return d.needs_attention
  return d.parse_status === 'NEEDS_REVIEW' || d.parse_status === 'FAILED'
}

export interface PointOfSaleRow {
  id: string
  location_name: string
  code: string
  name: string
  kind: string
  active: boolean
}

export interface TerminalRow {
  id: string
  code: string
  name: string
  preferred_point_of_sale_id: string | null
  preferred_point_of_sale_name: string | null
  active: boolean
}

export interface PaymentMethodRow { code: string; label: string; active: boolean }

/**
 * Une caisse du référentiel. Elle porte le RATTACHEMENT au point de vente :
 * c'est par elle qu'un libellé fournisseur (« la caisse n° 3 du rapport »)
 * retrouve la caisse de Gestion, puis son point de vente. Sans ce chaînon, un
 * montant lu reste un montant orphelin.
 */
export interface RegisterRow {
  id: string
  point_of_sale_id: string
  point_of_sale_name: string
  code: string
  name: string
  active: boolean
}

/** Les motifs d'écart du référentiel, servis par `/finance/references`. */
export interface DiscrepancyReasonRow { code: string; label: string; active: boolean }

/* ═══════════════ ce qu'un document source a livré ═══════════════ */

/**
 * Une ligne extraite d'un rapport.
 *
 * `register_id` NUL veut dire « ce libellé fournisseur ne désigne encore
 * aucune caisse de Gestion ». La ligne n'est alors ni jetée ni devinée : elle
 * est montrée à part, avec son libellé d'origine, pour qu'un humain fasse la
 * correspondance. « Ça ressemble à la caisse du bar » n'est pas une réponse.
 */
export interface SourceLineRow {
  id: string
  register_alias: string | null
  register_id: string | null
  register_name: string | null
  payment_method: string | null
  amount_cents: Cents
  label: string | null
}

export interface SourceReadingRow {
  id: string
  parser_version: string
  status: 'PARSED' | 'NEEDS_REVIEW' | 'FAILED'
  note: string | null
  created_at: string
  /** Ce que le lecteur n'a pas su ranger. Du texte, pas un chiffre. */
  unmapped: string[]
  lines: SourceLineRow[]
}

export interface SourceDocumentDetail extends SourceDocumentRow {
  readings: SourceReadingRow[]
}

/**
 * Une lecture EXPLOITABLE de la nuit : un document, la lecture qui a réussi,
 * et ses lignes. Une lecture « à vérifier » n'entre jamais ici — un document
 * que Gestion n'a pas su lire ne produit aucun chiffre.
 */
export interface LectureSource {
  documentId: string
  documentNom: string
  fournisseur: SourceProvider
  readingId: string
  parserVersion: string
  luLe: string
  lignes: SourceLineRow[]
}

export interface FinanceSettingsView {
  denominations_cents: number[]
  discrepancy_warn_cents: number
  discrepancy_alert_cents: number
}

/**
 * Coupures servies quand les réglages ne sont pas encore lisibles.
 *
 * Ce n'est PAS un chiffre inventé : une coupure est une case à remplir, pas un
 * montant. Elle vaut zéro tant que personne n'a compté, et l'écran le dit.
 * Le jeu reprend celui du cœur (`settings.ts`) : 200 € → 1 centime.
 */
export const COUPURES_DEFAUT = [20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1]

export const SEUILS_DEFAUT = { warn: 1000, alert: 5000 }

/* ═══════════════════════ lecture tolérante ═══════════════════════ */

export type Etat<T> =
  | { etat: 'chargement' }
  | { etat: 'ok'; valeur: T }
  | { etat: 'absent'; message: string }

const MSG_ABSENT = 'Cette information n’est pas encore servie par le serveur.'
const MSG_INTERDIT = 'Votre rôle ne donne pas accès à cette information.'
const MSG_RESEAU = 'Gestion n’a pas pu joindre le serveur.'

function objet(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

/** Un tableau, qu'il arrive nu ou sous l'une des clés proposées. */
export function tableauSous(v: unknown, ...cles: string[]): unknown[] | null {
  if (Array.isArray(v)) return v
  const o = objet(v)
  if (!o) return null
  for (const c of cles) { const x = o[c]; if (Array.isArray(x)) return x }
  return null
}

/** Un objet, qu'il arrive nu ou sous l'une des clés proposées. */
export function objetSous(v: unknown, ...cles: string[]): Record<string, unknown> | null {
  const o = objet(v)
  if (!o) return null
  for (const c of cles) { const x = objet(o[c]); if (x) return x }
  return o
}

/**
 * Lit une route et rend un ÉTAT, jamais une exception.
 *
 * La garde décide si la charge utile est exploitable. Elle rend `null` quand
 * ce n'est pas le cas — une route absente, un refus de permission et une
 * réponse d'une autre forme se ressemblent trop sur le réseau pour qu'on
 * distingue les trois à coup sûr, et les trois se disent de la même façon à
 * l'écran : « on ne sait pas », suivi de la raison.
 *
 * `signal` (facultatif) annule la requête elle-même ; une lecture annulée se
 * rend comme une coupure, que l'appelant qui l'a annulée n'écrit pas.
 */
export async function lireJson<T>(chemin: string, garde: (v: unknown) => T | null, signal?: AbortSignal): Promise<Etat<T>> {
  try {
    const brut = await apiGet<unknown>(chemin, { signal })
    const o = objet(brut)
    const err = o ? o['error'] : undefined
    if (typeof err === 'string') {
      return { etat: 'absent', message: err === 'FORBIDDEN' ? MSG_INTERDIT : MSG_ABSENT }
    }
    const v = garde(brut)
    return v === null ? { etat: 'absent', message: MSG_ABSENT } : { etat: 'ok', valeur: v }
  } catch {
    return { etat: 'absent', message: MSG_RESEAU }
  }
}

/** Gardes minimales : on vérifie l'identité de la forme, pas chaque champ. */
export function commeLignes<T>(cles: string[], obligatoire: string): (v: unknown) => T[] | null {
  return (v) => {
    const arr = tableauSous(v, ...cles)
    if (!arr) return null
    return arr.filter((x) => { const o = objet(x); return !!o && typeof o[obligatoire] === 'string' }) as T[]
  }
}

export function commeObjet<T>(cles: string[], obligatoire: string): (v: unknown) => T | null {
  return (v) => {
    const o = objetSous(v, ...cles)
    return o && typeof o[obligatoire] === 'string' ? (o as T) : null
  }
}

/* ═══════════════════════ dates et durées ═══════════════════════ */

const FMT_LONG = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
const FMT_COURT = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
const FMT_HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })

function capitaliser(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1) }

/** « Vendredi 4 septembre 2026 ». La date de la NUIT, pas d'un instant. */
export function nuitLongue(ymd: string): string {
  return capitaliser(FMT_LONG.format(new Date(`${ymd}T12:00:00Z`)))
}

/** « ven. 4 sept. » — pour une colonne de tableau. */
export function nuitCourte(ymd: string): string {
  return capitaliser(FMT_COURT.format(new Date(`${ymd}T12:00:00Z`)))
}

/** « 06:12 », toujours à l'heure de Paris : la nuit se compte ici. */
export function heureParis(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : FMT_HEURE.format(d)
}

/** « il y a 12 min », « il y a 3 h », « il y a 2 j ». Jamais « 0 ». */
export function ilYA(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const min = Math.floor((Date.now() - d.getTime()) / 60000)
  if (min < 1) return 'à l’instant'
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  const j = Math.floor(h / 24)
  return j === 1 ? 'hier' : `il y a ${j} jours`
}

/** Le jour d'aujourd'hui à Paris, en AAAA-MM-JJ. Sert aux bornes de requête. */
export function aujourdhuiParis(): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
}

export function ajouterJours(ymd: string, n: number): string {
  const d = new Date(`${ymd}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/* ═══════════════════════ arithmétique de centimes ═══════════════════════ */

/** Le pilote PostgreSQL rend un `bigint` en chaîne : on l'accepte tel quel. */
export function enCentimes(c: Cents): number | null {
  if (c === null || c === undefined) return null
  const n = typeof c === 'string' ? Number(c) : c
  return Number.isFinite(n) ? n : null
}

/**
 * Somme qui rend `null` tant qu'AUCUNE valeur n'est renseignée.
 *
 * `sommeCents` compte une absence pour zéro — c'est ce qu'il faut pour un
 * sous-total de coupures. Ici c'est l'inverse qu'on veut : un point de vente
 * dont aucune ligne ne porte de source n'a pas « 0,00 € de source », il n'a
 * PAS DE SOURCE. Afficher zéro donnerait à un manque l'apparence d'un fait, et
 * l'écart calculé contre ce zéro serait un écart inventé.
 */
export function sommeSiRenseignee(valeurs: Cents[]): number | null {
  let total = 0
  let renseignee = false
  for (const v of valeurs) {
    const n = enCentimes(v)
    if (n === null) continue
    total += n
    renseignee = true
  }
  return renseignee ? total : null
}

/* ═══════════════ la source, regroupée par point de vente ═══════════════ */

/*
 * Codes techniques des moyens de paiement. Ils servent à RANGER une colonne,
 * jamais à s'afficher : le libellé vient toujours du référentiel. Ce sont les
 * mêmes que ceux de l'étape « point de vente » — en inventer d'autres aurait
 * créé une seconde convention pour la même chose.
 */
export const MOYEN_CARTE = 'CB'
export const MOYEN_ESPECES = 'ESPECES'
export const MOYEN_OFFERT = 'OFFERT'

export interface SourceParPos {
  /** `null` : la caisse du fournisseur n'est rattachée à aucun point de vente. */
  point_of_sale_id: string | null
  point_of_sale_name: string | null
  /** Libellés fournisseur regroupés ici — second rang, jamais le nom courant. */
  alias: string[]
  carte_cents: number
  especes_cents: number
  offerts_cents: number
  /** Tout le reste, conservé AVEC son libellé : rien n'est ignoré. */
  autres: Array<{ libelle: string; cents: number }>
  total_cents: number
}

/**
 * Range les lignes d'une lecture par point de vente.
 *
 * DEUX REFUS DE DEVINER commandent cette fonction. Une caisse dont le libellé
 * fournisseur n'est rattaché à aucune caisse de Gestion ne rejoint AUCUN point
 * de vente : elle sort dans son propre groupe, sous son libellé d'origine,
 * pour qu'un humain tranche. Et un moyen de paiement hors carte/espèces/offert
 * n'est pas fondu dans un « divers » anonyme : il garde son libellé et son
 * montant, faute de quoi un chèque de 400 € disparaîtrait dans un total.
 */
export function sourceParPointDeVente(
  lignes: SourceLineRow[],
  caisses: RegisterRow[],
  libelles: Record<string, string> = {},
): SourceParPos[] {
  const parCaisse = new Map(caisses.map((c) => [c.id, c]))
  const groupes = new Map<string, SourceParPos>()

  for (const l of lignes) {
    const montant = enCentimes(l.amount_cents)
    // Une ligne dont le montant est illisible n'est pas une ligne à zéro.
    if (montant === null) continue
    const caisse = l.register_id === null ? undefined : parCaisse.get(l.register_id)
    const libelleCaisse = l.register_alias ?? l.register_name
    const cle = caisse ? caisse.point_of_sale_id : `non-rattachee:${libelleCaisse ?? l.id}`

    let g = groupes.get(cle)
    if (!g) {
      g = {
        point_of_sale_id: caisse ? caisse.point_of_sale_id : null,
        point_of_sale_name: caisse ? caisse.point_of_sale_name : null,
        alias: [], carte_cents: 0, especes_cents: 0, offerts_cents: 0, autres: [], total_cents: 0,
      }
      groupes.set(cle, g)
    }
    if (libelleCaisse !== null && libelleCaisse !== undefined && !g.alias.includes(libelleCaisse)) {
      g.alias.push(libelleCaisse)
    }

    if (l.payment_method === MOYEN_CARTE) g.carte_cents += montant
    else if (l.payment_method === MOYEN_ESPECES) g.especes_cents += montant
    else if (l.payment_method === MOYEN_OFFERT) g.offerts_cents += montant
    else {
      const libelle = (l.payment_method === null ? null : libelles[l.payment_method] ?? l.payment_method)
        ?? l.label ?? 'Moyen de paiement non identifié'
      const deja = g.autres.find((a) => a.libelle === libelle)
      if (deja) deja.cents += montant
      else g.autres.push({ libelle, cents: montant })
    }
    g.total_cents += montant
  }

  /* Les caisses rattachées d'abord, les autres ensuite : une correspondance
     qui manque doit se voir, pas se ranger discrètement au milieu. */
  return [...groupes.values()].sort((a, b) => {
    if ((a.point_of_sale_id === null) !== (b.point_of_sale_id === null)) {
      return a.point_of_sale_id === null ? 1 : -1
    }
    const na = a.point_of_sale_name ?? a.alias[0] ?? ''
    const nb = b.point_of_sale_name ?? b.alias[0] ?? ''
    return na.localeCompare(nb, 'fr')
  })
}

/* ═══════════════════════ montants à l'écran ═══════════════════════ */

/** Un montant en cellule : tabulaire, à droite, « — » quand il n'existe pas. */
export function Montant(props: { cents: Cents; fort?: boolean; faible?: boolean }): ReactNode {
  const cls = `fi-money${props.fort ? ' fi-money--fort' : ''}${props.faible ? ' fi-money--faible' : ''}`
  return <span className={cls}>{formatMoney(props.cents)}</span>
}

/**
 * L'écart, avec son signe TOUJOURS écrit et une couleur qui ne porte jamais
 * l'information seule. Un écart nul se dit « 0,00 € », pas par une case vide.
 */
export function Ecart(props: { cents: Cents; seuils?: { warn: number; alert: number } }): ReactNode {
  const { texte, ton } = formatEcart(props.cents)
  const n = typeof props.cents === 'string' ? Number(props.cents) : props.cents
  const abs = typeof n === 'number' && Number.isFinite(n) ? Math.abs(n) : 0
  const s = props.seuils ?? SEUILS_DEFAUT
  const ampleur = abs === 0 ? '' : abs >= s.alert ? ' fi-ecart--fort' : abs >= s.warn ? ' fi-ecart--tiede' : ''
  return <span className={`fi-ecart fi-ecart--${ton}${ampleur}`}>{texte}</span>
}

/**
 * L'écart d'un POINT DE VENTE, d'un sous-total ou de la nuit : sa couleur dit
 * le SEUIL (vert sous la vigilance, ambre au-delà, rouge au-delà de l'alerte),
 * pas le signe — +1 059 € à l'Accueil n'est pas une bonne nouvelle verte.
 * L'écart d'un moyen de paiement (carte, espèces) garde `Ecart`, au signe.
 */
export function EcartSeuil(props: { cents: Cents; seuils?: { warn: number; alert: number } }): ReactNode {
  const { texte } = formatEcart(props.cents)
  const ton = tonSeuil(props.cents, props.seuils ?? SEUILS_DEFAUT)
  const poids = ton === 'fort' ? ' fi-ecart--fort' : ton === 'tiede' ? ' fi-ecart--tiede' : ''
  return <span className={`fi-ecart fi-seuil--${ton}${poids}`}>{texte}</span>
}

/* ═══════════════════════ saisies ═══════════════════════ */

/**
 * Un montant se saisit en euros et se transmet en centimes.
 *
 * Le champ garde la frappe BRUTE : reformater « 13, » en « 13,00 » pendant que
 * la personne tape lui arracherait sa saisie des mains. La conversion n'a lieu
 * que si la frappe est complète ; une frappe illisible ne vaut pas zéro, elle
 * vaut « pas encore un montant » et le champ le montre.
 */
export function MoneyInput(props: {
  cents: number | null
  onChange: (cents: number | null) => void
  ariaLabel: string
  /** Le moment naturel d'enregistrer : la personne a fini sa saisie. */
  onBlur?: (() => void) | undefined
  placeholder?: string
  disabled?: boolean
  className?: string
}) {
  const rendu = (c: number | null): string => (c === null ? '' : (c / 100).toFixed(2).replace('.', ','))
  const [brut, setBrut] = useState(() => rendu(props.cents))
  useEffect(() => {
    if (parseMoney(brut) !== props.cents) setBrut(rendu(props.cents))
    // Le champ ne se resynchronise que si la valeur EXTÉRIEURE a changé.
  }, [props.cents]) // eslint-disable-line react-hooks/exhaustive-deps

  const illisible = brut.trim() !== '' && parseMoney(brut) === null
  return (
    <span className="fi-moneyfield">
      <input
        className={`lc-input fi-moneyfield__input${illisible ? ' lc-input--error' : ''}${props.className ? ` ${props.className}` : ''}`}
        inputMode="decimal"
        aria-label={props.ariaLabel}
        aria-invalid={illisible}
        placeholder={props.placeholder ?? '0,00'}
        disabled={props.disabled}
        value={brut}
        onBlur={props.onBlur}
        onChange={(e) => {
          const v = e.target.value
          setBrut(v)
          if (v.trim() === '') { props.onChange(null); return }
          const c = parseMoney(v)
          if (c !== null) props.onChange(c)
        }}
      />
      <span className="fi-moneyfield__unit" aria-hidden="true">€</span>
    </span>
  )
}

/** La règle des douze caractères, portée par la base ET revérifiée ici. */
export function motifSuffisant(s: string): boolean { return s.trim().length >= 12 }

/**
 * Le champ de motif. Il ne dit pas « invalide » : il dit combien il manque.
 * Un refus muet à 6 h du matin fait inventer une explication au lieu de la
 * donner.
 */
export function MotifField(props: {
  id: string
  valeur: string
  onChange: (v: string) => void
  label?: string
  aide?: string
}) {
  const manque = Math.max(0, 12 - props.valeur.trim().length)
  return (
    <div className="lc-field">
      <label className="lc-label" htmlFor={props.id}>{props.label ?? 'Motif de la correction'}</label>
      <textarea
        id={props.id}
        className="lc-input fi-motif"
        rows={2}
        value={props.valeur}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder="Ce qui s’est passé, en une phrase."
      />
      <span className={manque > 0 ? 'lc-helper lc-helper--error' : 'lc-helper'}>
        {manque > 0
          ? `Encore ${manque} caractère${manque > 1 ? 's' : ''} : une correction sans explication ne se retrouve pas trois mois plus tard.`
          : (props.aide ?? 'Cette explication reste attachée au montant, pour toujours.')}
      </span>
    </div>
  )
}

/* ═══════════════════════ états honnêtes ═══════════════════════ */

/** En cours de lecture. Ne dit RIEN du contenu : on ne sait pas encore. */
export function EnCours(props: { quoi: string }) {
  return <p className="fi-empty">Lecture {props.quoi} en cours.</p>
}

/**
 * L'information n'est pas arrivée. On écrit pourquoi, et on n'écrit pas
 * « 0 € » — ne pas savoir et valoir zéro sont deux choses différentes.
 */
export function Indisponible(props: { titre: string; message: string; children?: ReactNode }) {
  return (
    <div className="fi-empty">
      <div className="fi-empty__title">{props.titre}</div>
      <p>{props.message}</p>
      {props.children}
    </div>
  )
}
